import api from "@/utils/axios";
import type { BroadcastMessage } from "@/components/BroadcastBanner";
import type {
  BroadcastApiType,
  BroadcastMessageRequest,
  BroadcastMessageResponse,
} from "@/types/broadcast.types";

/**
 * API client for broadcast messages.
 */
const toUiType = (type: BroadcastApiType): BroadcastMessage["type"] =>
  type.toLowerCase() as BroadcastMessage["type"];

/**
 * Maps API broadcast response into the UI banner model.
 */
export const toBroadcastMessage = (
  broadcast: BroadcastMessageResponse
): BroadcastMessage => ({
  id: broadcast.id,
  subject: {
    de: broadcast.subjectDe,
    en: broadcast.subjectEn,
  },
  message: {
    de: broadcast.messageDe ?? "",
    en: broadcast.messageEn ?? undefined,
  },
  type: toUiType(broadcast.type),
  startTime: broadcast.startTime,
  endTime: broadcast.endTime,
  createdBy: broadcast.createdBy ?? "admin",
});

export const broadcastService = {
  /**
   * Loads currently visible broadcast messages.
   */
  async getVisibleBroadcasts(): Promise<BroadcastMessageResponse[]> {
    const response = await api.get<BroadcastMessageResponse[]>("/broadcasts");
    return response.data;
  },

  /**
   * Loads all broadcast messages for the admin dashboard.
   */
  async getAdminBroadcasts(): Promise<BroadcastMessageResponse[]> {
    const response = await api.get<BroadcastMessageResponse[]>(
      "/admin/broadcasts"
    );
    return response.data;
  },

  /**
   * Creates a new broadcast message.
   */
  async createBroadcast(
    data: BroadcastMessageRequest
  ): Promise<BroadcastMessageResponse> {
    const response = await api.post<BroadcastMessageResponse>(
      "/admin/broadcasts",
      data
    );
    return response.data;
  },
};
